import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { FiEdit2, FiTrash2, FiPlus, FiX } from 'react-icons/fi';

const CalculationDetailsSection = ({
    calculation,
    isEditing,
    onEdit,
    onCancel,
    onSave,
    loading = false
}) => {
    const { t } = useTranslation();
    if (!calculation) return null;

    const [formData, setFormData] = useState({
        calculationName: calculation.calculationName || '',
        transportationValues: [...(calculation.transportationValues || [])],
        forwardingAgentValues: [...(calculation.forwardingAgentValues || [])]
    });

    useEffect(() => {
        // Only update form data when not editing or when calculation ID changes
        if (!isEditing) {
            setFormData({
                calculationName: calculation.calculationName || '',
                transportationValues: [...(calculation.transportationValues || [])],
                forwardingAgentValues: [...(calculation.forwardingAgentValues || [])]
            });
        }
    }, [calculation.id, calculation.calculationName, calculation.transportationValues, calculation.forwardingAgentValues, isEditing]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleArrayValueChange = (arrayName, index, value) => {
        const newArray = [...formData[arrayName]];
        newArray[index] = parseFloat(value) || 0;
        setFormData(prev => ({
            ...prev,
            [arrayName]: newArray
        }));
    };

    const addArrayItem = (arrayName) => {
        setFormData(prev => ({
            ...prev,
            [arrayName]: [...prev[arrayName], 0]
        }));
    };

    const removeArrayItem = (arrayName, index) => {
        const newArray = formData[arrayName].filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            [arrayName]: newArray
        }));
    };

    const handleSave = () => {
        const payload = {
            calculationName: { value: formData.calculationName },
            transportationValues: { value: formData.transportationValues },
            forwardingAgentValues: { value: formData.forwardingAgentValues }
        };
        onSave(payload);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2
        }).format(value || 0);
    };

    // Get values from the API response
    const {
        totalSellSum = 0,
        totalStockPrice = 0,
        productProfit = 0,
        items = [],
        transportationValues = [0],
        forwardingAgentValues = [0]
    } = calculation;

    const transportationValue = transportationValues[0] || 0;
    const forwardingAgentValue = forwardingAgentValues[0] || 0;

    // Calculate total costs
    const totalTransportationCost = transportationValues.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const totalForwardingAgentCost = forwardingAgentValues.reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    const totalCustomsCost = items.reduce((sum, item) => sum + (parseFloat(item.customsValue) || 0), 0);
    
    // Calculate total profit with all costs included
    const totalProfitWithCosts = productProfit - totalTransportationCost - totalForwardingAgentCost - totalCustomsCost;

    const renderField = (label, value) => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <div className="text-gray-900">{value}</div>
        </div>
    );

    const renderEditField = (label, name, value, onChange, type = 'text') => (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
        </div>
    );

    const renderArrayField = (label, arrayName, values, onChange, onAdd, onRemove) => (
        <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                <button
                    type="button"
                    onClick={() => onAdd(arrayName)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                    <FiPlus className="inline mr-1" /> Add
                </button>
            </div>
            {values.length > 0 ? (
                values.map((value, index) => (
                    <div key={index} className="flex items-center mb-2">
                        <input
                            type="number"
                            step="0.01"
                            value={value}
                            onChange={(e) => onChange(arrayName, index, e.target.value)}
                            className="flex-1 mr-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                        <button
                            type="button"
                            onClick={() => onRemove(arrayName, index)}
                            className="text-red-600 hover:text-red-800"
                        >
                            <FiX />
                        </button>
                    </div>
                ))
            ) : (
                <p className="text-sm text-gray-500 italic">No values added</p>
            )}
        </div>
    );

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
                {isEditing ? (
                    <div className="w-full">
                        <input
                            type="text"
                            name="calculationName"
                            value={formData.calculationName}
                            onChange={handleInputChange}
                            className="text-2xl font-bold text-gray-800 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder={t('calculation.calculationName')}
                        />
                    </div>
                ) : (
                    <h2 className="text-2xl font-bold text-gray-800">
                        {calculation.calculationName || `${t('calculation.title')} #${calculation.id}`}
                    </h2>
                )}
                <div className="text-sm text-gray-500">
                    {t('common.createdAt')}: {format(new Date(calculation.createdAt), 'PPpp')}
                </div>
            </div>

            <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-700">{t('calculation.title')}</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                        <thead>
                            <tr className="bg-gray-100 text-left text-gray-600">
                                <th className="py-2 px-4">{t('calculation.productCode')}</th>
                                <th className="py-2 px-4 text-right">{t('calculation.totalSellSum')}</th>
                                <th className="py-2 px-4 text-right">{t('calculation.totalStockPrice')}</th>
                                <th className="py-2 px-4 text-right">{t('calculation.productProfit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                                    <td className="py-3 px-4">{item.productCode?.trim()}</td>
                                    <td className="py-3 px-4 text-right">{formatCurrency(item.grandTotalSell)}</td>
                                    <td className="py-3 px-4 text-right">{formatCurrency(item.grandTotalStockPrice)}</td>
                                    <td className="py-3 px-4 text-right font-medium">
                                        {formatCurrency(item.itemProfit)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Summary Section */}
            <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 text-gray-700">{t('calculation.costsIncluded')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-50 p-4 rounded border">
                        {isEditing ? (
                            <>
                                <h4 className="font-medium text-gray-700 mb-3">{t('calculation.transportationCosts')}</h4>
                                {renderArrayField(
                                    t('calculation.transportationCosts'),
                                    'transportationValues',
                                    formData.transportationValues,
                                    handleArrayValueChange,
                                    addArrayItem,
                                    removeArrayItem
                                )}
                                <div className="pt-2 mt-2 border-t border-gray-200">
                                    <div className="flex justify-between items-center font-semibold">
                                        <span>Total:</span>
                                        <span>
                                            {formatCurrency(
                                                formData.transportationValues.reduce(
                                                    (sum, val) => sum + (parseFloat(val) || 0), 0
                                                )
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <h4 className="font-medium text-gray-700 mb-3">{t('calculation.transportationCosts')}</h4>
                                <div className="space-y-2">
                                    {transportationValues.length > 0 ? (
                                        transportationValues.map((value, index) => (
                                            <div key={`transport-${index}`} className="flex justify-between items-center">
                                                <span className="text-gray-600">{index + 1}:</span>
                                                <span className="font-medium">{formatCurrency(value)}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-gray-500">{t('common.noData')}</p>
                                    )}
                                    <div className="pt-2 mt-2 border-t border-gray-200">
                                        <div className="flex justify-between items-center font-semibold">
                                            <span>Total:</span>
                                            <span>{formatCurrency(transportationValues.reduce((sum, val) => sum + (parseFloat(val) || 0), 0))}</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="bg-gray-50 p-4 rounded border">
                        {isEditing ? (
                            <>
                                <h4 className="font-medium text-gray-700 mb-3">{t('calculation.forwardingAgentCosts')}</h4>
                                {renderArrayField(
                                    t('calculation.forwardingAgentCosts'),
                                    'forwardingAgentValues',
                                    formData.forwardingAgentValues,
                                    handleArrayValueChange,
                                    addArrayItem,
                                    removeArrayItem
                                )}
                                <div className="pt-2 mt-2 border-t border-gray-200">
                                    <div className="flex justify-between items-center font-semibold">
                                        <span>Total:</span>
                                        <span>
                                            {formatCurrency(
                                                formData.forwardingAgentValues.reduce(
                                                    (sum, val) => sum + (parseFloat(val) || 0), 0
                                                )
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <h4 className="font-medium text-gray-700 mb-3">{t('calculation.forwardingAgentCosts')}</h4>
                                <div className="space-y-2">
                                    {forwardingAgentValues.length > 0 ? (
                                        forwardingAgentValues.map((value, index) => (
                                            <div key={`forwarding-${index}`} className="flex justify-between items-center">
                                                <span className="text-gray-600">{index + 1}:</span>
                                                <span className="font-medium">{formatCurrency(value)}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-gray-500">{t('common.noData')}</p>
                                    )}
                                    <div className="pt-2 mt-2 border-t border-gray-200">
                                        <div className="flex justify-between items-center font-semibold">
                                            <span>Total:</span>
                                            <span>{formatCurrency(forwardingAgentValues.reduce((sum, val) => sum + (parseFloat(val) || 0), 0))}</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {isEditing && (
                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            disabled={loading}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            disabled={loading}
                        >
                            {loading ? t('common.saving') : t('common.save')}
                        </button>
                    </div>
                )}
            </div>

            {/* Totals Section */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-green-50 p-4 rounded border-l-4 border-green-500">
                    <h4 className="font-medium text-gray-700 mb-2">{t('calculation.totalSellSum')}</h4>
                    <p className="text-xl font-bold text-green-600">
                        {formatCurrency(totalSellSum)}
                    </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-500">
                    <h4 className="font-medium text-gray-700 mb-2">{t('calculation.totalStockPrice')}</h4>
                    <p className="text-xl font-bold text-yellow-600">
                        {formatCurrency(totalStockPrice)}
                    </p>
                </div>
                <div className="bg-blue-50 p-4 rounded border-l-4 border-blue-500">
                    <h4 className="font-medium text-gray-700 mb-2">{t('calculation.totalProfit')}</h4>
                    <p className="text-xl font-bold text-blue-600">
                        {formatCurrency(productProfit)}
                    </p>
                </div>
                <div className="bg-purple-50 p-4 rounded border-l-4 border-purple-500">
                    <h4 className="font-medium text-gray-700 mb-2">{t('calculation.totalProfit')} ({t('calculation.costsIncluded')})</h4>
                    <p className="text-xl font-bold text-purple-600">
                        {formatCurrency(totalProfitWithCosts)}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CalculationDetailsSection;
